import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import useApi from "../../../hooks/useApi";
import { hrefSeguro } from "../../../utils/hrefSeguro";

// Envíos de 5 RC de cada difusor, como columnas de la tabla de Verificación
// (Envío de 5 RC, Estado 5 RC y Acciones 5 RC).
//
// Se le puede activar a cualquier difusor, lo haya solicitado o no: el
// diálogo trae precargado el canal que mandó en su solicitud o, si no la
// mandó, el de su perfil, y el admin lo confirma o corrige. El canal hace
// falta: al aprobar un envío de 5 RC, el link de la publicación se valida
// contra él. Reglas en Backend/src/utils/canales5RC.js.

const ESTADOS = {
  pendiente: { label: "Por revisar", color: "warning" },
  sin_solicitud: { label: "Sin activar", color: "default" },
  aprobada: { label: "Activo", color: "success" },
  rechazada: { label: "Rechazada", color: "error" },
  desactivada: { label: "Desactivada", color: "default" },
};

const MENSAJE_MIN = 10;
const MAX_CANALES = 5;

const mismosCanales = (a, b) =>
  a.length === b.length && a.every((url, i) => url === b[i]);

export default function useEnvios5RC(showSnack) {
  const { loadApi, loadingApi } = useApi();
  const [porUsuario, setPorUsuario] = useState(null); // userId → activación
  // { difusor, fila, estado: "rechazada" | "desactivada" } → diálogo con mensaje
  const [objetivo, setObjetivo] = useState(null);
  const [mensaje, setMensaje] = useState("");
  // { difusor, fila } a revisar, activar o cuyos canales se corrigen
  const [activando, setActivando] = useState(null);
  const [canales, setCanales] = useState([""]);

  const cargar = () =>
    loadApi("admin/activaciones-5rc", true, "get")
      .then((res) =>
        setPorUsuario(new Map((res.data || []).map((f) => [f.userId, f])))
      )
      .catch((e) => showSnack(e.message, "error"));

  useEffect(() => {
    cargar();
  }, []);

  const filaDe = (difusor) => porUsuario?.get(difusor.userId);
  const cargandoFila = (fila) =>
    loadingApi.includes(`put__admin/activaciones-5rc/${fila.userId}`);

  const decidir = (fila, cuerpo) =>
    loadApi(`admin/activaciones-5rc/${fila.userId}`, true, "put", cuerpo)
      .then((res) => {
        showSnack(res.data.message);
        setObjetivo(null);
        setActivando(null);
        cargar();
      })
      .catch((e) => {
        showSnack(e.message, "error");
        cargar();
      });

  const abrirActivacion = (difusor, fila) => {
    const base = fila.canales.length ? fila.canales : fila.sugeridos;
    setCanales(base.length ? base.map((c) => c.url) : [""]);
    setActivando({ difusor, fila });
  };

  const abrirMensaje = (difusor, fila, estado) => {
    setObjetivo({ difusor, fila, estado });
    setMensaje("");
  };

  const guardarActivacion = () => {
    const { fila } = activando;
    const limpios = canales.map((c) => c.trim()).filter(Boolean);
    // Editar canales sin cambiar ninguno: no hay nada que guardar ni que
    // avisarle al difusor.
    if (
      fila.estado === "aprobada" &&
      mismosCanales(
        limpios,
        fila.canales.map((c) => c.url)
      )
    ) {
      showSnack("No cambiaste ningún canal: quedan los mismos.", "info");
      setActivando(null);
      return;
    }
    // Si aprueba la solicitud tal como la mandó el difusor, se manda la
    // versión: el backend rechaza la aprobación si el difusor cambió sus
    // canales mientras el admin los revisaba.
    const sinCambios =
      fila.estado === "pendiente" &&
      mismosCanales(
        limpios,
        fila.canales.map((c) => c.url)
      );
    decidir(
      fila,
      sinCambios
        ? { estado: "aprobada", version: fila.version }
        : { estado: "aprobada", canales: limpios }
    );
  };

  const porRevisar = (difusor) => filaDe(difusor)?.estado === "pendiente";

  const renderPlan = (difusor) => filaDe(difusor)?.plan || "—";

  const renderEstado = (difusor) => {
    const fila = filaDe(difusor);
    if (!fila) return "—";
    return (
      <>
        <Chip
          label={ESTADOS[fila.estado]?.label || fila.estado}
          color={ESTADOS[fila.estado]?.color || "default"}
          size="small"
        />
        {fila.feedback && (
          <Typography
            variant="caption"
            color="GrayText"
            display="block"
            mt={0.5}
            mx="auto"
            maxWidth={110}
            title={fila.feedback}
          >
            Último mensaje enviado
          </Typography>
        )}
      </>
    );
  };

  const renderAcciones = (difusor) => {
    const fila = filaDe(difusor);
    if (!fila) return null;
    // Un botón debajo del otro, para que la columna no le quite ancho a las
    // de verificación.
    return (
      <Stack gap={1} alignItems="center" sx={{ "& .MuiButton-root": { whiteSpace: "nowrap" } }}>
        {fila.estado === "pendiente" && (
          <Button
            size="small"
            color="success"
            onClick={() => abrirActivacion(difusor, fila)}
          >
            Revisar
          </Button>
        )}
        {["sin_solicitud", "rechazada", "desactivada"].includes(fila.estado) && (
          <Button
            size="small"
            color="success"
            onClick={() => abrirActivacion(difusor, fila)}
          >
            Activar
          </Button>
        )}
        {fila.estado === "aprobada" && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => abrirActivacion(difusor, fila)}
          >
            Editar canales
          </Button>
        )}
        {fila.estado === "pendiente" && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={() => abrirMensaje(difusor, fila, "rechazada")}
          >
            Rechazar
          </Button>
        )}
        {fila.estado === "aprobada" && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={() => abrirMensaje(difusor, fila, "desactivada")}
          >
            Desactivar
          </Button>
        )}
      </Stack>
    );
  };

  const nombreDe = (difusor) => difusor.nombre_media || difusor.nombre;

  const renderDialogoActivacion = () => {
    const { difusor, fila } = activando;
    const limpios = canales.map((c) => c.trim()).filter(Boolean);
    const titulo = {
      pendiente: "Solicitud de envíos de 5 RC de",
      aprobada: "Canales de 5 RC de",
    };
    const boton = { pendiente: "Aprobar", aprobada: "Guardar canales" };
    return (
      <>
        <DialogTitle>
          {titulo[fila.estado] || "Activar envíos de 5 RC de"}{" "}
          {nombreDe(difusor)}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            <Typography variant="body2" color="GrayText">
              {fila.tipo || "Difusor"} · Envío{" "}
              {fila.plan === "Editorial" ? "Editorial" : "de Mención"}. Puede
              tener: {fila.permitidas.map((p) => p.nombre).join(", ")}. Al
              aprobar un envío de 5 RC, al menos un link tendrá que ser de
              estos canales.
            </Typography>
            {difusor.estado !== "verificado" && (
              <Alert severity="warning">
                Su medio todavía no está verificado: hasta que lo verifiques no
                aparece en el listado de los artistas.
              </Alert>
            )}
            {fila.estado === "aprobada" ? (
              <Alert severity="info">
                Estos son sus canales aprobados. Corrige o agrega uno y guarda:
                el difusor recibe un aviso con los nuevos.
              </Alert>
            ) : fila.estado === "pendiente" ? (
              <Alert severity="info">
                Estos son los canales que mandó. Ábrelos y confirma que existen
                y que son suyos; si alguno está mal, corrígelo antes de aprobar.
              </Alert>
            ) : fila.canales.length === 0 && fila.sugeridos.length > 0 ? (
              <Alert severity="info">
                Precargado desde su perfil. Ábrelo y confirma que es suyo antes
                de activar.
              </Alert>
            ) : fila.canales.length === 0 ? (
              <Alert severity="info">
                No tiene en su perfil un canal de los que puede tener. Escríbelo
                para activarlo.
              </Alert>
            ) : null}
            {canales.map((canal, i) => (
              <Stack key={i} direction="row" gap={1} alignItems="center">
                <TextField
                  size="small"
                  fullWidth
                  label={`Canal ${i + 1}`}
                  value={canal}
                  inputProps={{ "aria-label": `Canal ${i + 1}` }}
                  placeholder={
                    fila.plan === "Editorial" ? "https://susitio.com" : "https://…"
                  }
                  onChange={(e) =>
                    setCanales((prev) =>
                      prev.map((c, j) => (j === i ? e.target.value : c))
                    )
                  }
                />
                <IconButton
                  size="small"
                  component="a"
                  href={hrefSeguro(canal.trim()) || undefined}
                  target="_blank"
                  rel="nofollow noreferrer"
                  disabled={!canal.trim()}
                  aria-label={`Abrir canal ${i + 1}`}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
                {canales.length > 1 && (
                  <IconButton
                    size="small"
                    aria-label={`Quitar canal ${i + 1}`}
                    onClick={() =>
                      setCanales((prev) => prev.filter((_, j) => j !== i))
                    }
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}
            {fila.permitidas.length > 1 && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                sx={{ alignSelf: "flex-start" }}
                disabled={canales.length >= MAX_CANALES}
                onClick={() => setCanales((prev) => [...prev, ""])}
              >
                Agregar otro canal
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button variant="text" onClick={() => setActivando(null)}>
            Cancelar
          </Button>
          <LoadingButton
            color="success"
            disabled={limpios.length === 0}
            loading={cargandoFila(fila)}
            onClick={guardarActivacion}
          >
            {boton[fila.estado] || "Activar"}
          </LoadingButton>
        </DialogActions>
      </>
    );
  };

  const dialogos = (
    <>
      {/* Revisar / activar / corregir canales */}
      <Dialog
        open={Boolean(activando)}
        onClose={() => setActivando(null)}
        fullWidth
        maxWidth="sm"
      >
        {activando && renderDialogoActivacion()}
      </Dialog>

      {/* Rechazar / desactivar, con mensaje */}
      <Dialog
        open={Boolean(objetivo)}
        onClose={() => setObjetivo(null)}
        fullWidth
        maxWidth="sm"
      >
        {objetivo && (
          <>
            <DialogTitle>
              {objetivo.estado === "rechazada" ? "Rechazar" : "Desactivar"} los
              envíos de 5 RC de {nombreDe(objetivo.difusor)}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="GrayText" mb={2}>
                Mientras no esté activo, los artistas solo le pueden mandar
                Envíos Sociales (1 RC) y su Direct Link no se publica. El
                difusor recibirá este mensaje por correo y notificación.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                multiline
                rows={4}
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                inputProps={{ maxLength: 1000, "aria-label": "Mensaje para el difusor" }}
                placeholder="Ej.: El link de tu sitio no abre; registra la página donde publicas tus notas."
                helperText={`Mínimo ${MENSAJE_MIN} caracteres.`}
              />
            </DialogContent>
            <DialogActions sx={{ pb: 2, px: 3 }}>
              <Button variant="text" onClick={() => setObjetivo(null)}>
                Cancelar
              </Button>
              <LoadingButton
                color="error"
                disabled={mensaje.trim().length < MENSAJE_MIN}
                loading={cargandoFila(objetivo.fila)}
                onClick={() =>
                  decidir(objetivo.fila, {
                    estado: objetivo.estado,
                    feedback: mensaje.trim(),
                    version: objetivo.fila.version,
                  })
                }
              >
                {objetivo.estado === "rechazada" ? "Rechazar" : "Desactivar"}
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );

  const cuantosPorRevisar = porUsuario
    ? [...porUsuario.values()].filter((f) => f.estado === "pendiente").length
    : 0;

  return {
    porRevisar,
    cuantosPorRevisar,
    renderPlan,
    renderEstado,
    renderAcciones,
    dialogos,
  };
}
